// src/utils/saveProvider.js

import React, { createContext, useContext, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
  query,
  where,
} from 'firebase/firestore';
import { create } from 'zustand';

const useSaveStore = create((set, get) => ({
  userSavedListings: new Set(),
  isInitialized: false,
  unsubscribe: null,

  initialize: () => {
    const { unsubscribe: currentUnsub } = get();
    if (currentUnsub) {
      currentUnsub();
    }

    let unsubLikes = null;

    const authUnsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        if (unsubLikes) {
          unsubLikes();
          unsubLikes = null;
        }
        await get().initializeUserSaves(user.uid);
      } else {
        set({
          userSavedListings: new Set(),
          isInitialized: true,
        });
      }
    });

    set({
      unsubscribe: () => {
        authUnsubscribe();
        if (unsubLikes) {
          unsubLikes();
        }
      },
    });
  },

  initializeUserSaves: async (userId) => {
    try {
      const q = query(
        collection(db, 'listings'),
        where('savedBy', 'array-contains', userId)
      );
      const snap = await getDocs(q);
      const savedSet = new Set(snap.docs.map(doc => doc.id));
      set({
        userSavedListings: savedSet,
        isInitialized: true,
      });
    } catch (error) {
      console.error('SaveProvider init error:', error);
      set({ isInitialized: true });
    }
  },

  isSaved: (listingId) => {
    return get().userSavedListings.has(listingId);
  },

  toggleSave: async ({
    listingId,
    listingOwnerId,
    listingTitle,
    thumbnailUrl,
    listingImageUrl,
  }) => {
    const user = auth.currentUser;
    if (!user) return false;

    const isCurrentlySaved = get().userSavedListings.has(listingId);
    const listingRef = doc(db, 'listings', listingId);

    // Optimistic UI update
    const { userSavedListings } = get();
    if (isCurrentlySaved) {
      userSavedListings.delete(listingId);
    } else {
      userSavedListings.add(listingId);
    }
    set({ userSavedListings: new Set(userSavedListings) });

    try {
      if (isCurrentlySaved) {
        await updateDoc(listingRef, {
          savedBy: arrayRemove(user.uid),
        });
      } else {
        await updateDoc(listingRef, {
          savedBy: arrayUnion(user.uid),
        });

        // Send save notification only when saving (not unsaving)
        if (listingOwnerId !== user.uid) {
          try {
            const { sendSaveNotification } = await import(
              '../services/notificationService'
            );
            await sendSaveNotification({
              listingOwnerId,
              listingId,
              listingTitle,
              thumbnailUrl,
              listingImageUrl,
            });
          } catch (error) {
            console.error('Failed to send save notification:', error);
          }
        }
      }
      return true;
    } catch (error) {
      // Revert on error
      const { userSavedListings: currentSaved } = get();
      if (isCurrentlySaved) {
        currentSaved.add(listingId);
      } else {
        currentSaved.delete(listingId);
      }
      set({ userSavedListings: new Set(currentSaved) });
      console.error('SaveProvider toggleSave error:', error);
      return false;
    }
  },

  cleanup: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
    }
    set({
      userSavedListings: new Set(),
      isInitialized: false,
      unsubscribe: null,
    });
  },
}));

// React Hook for using the save store
export const useSave = () => {
  const store = useSaveStore();
  return {
    isSaved: store.isSaved,
    toggleSave: store.toggleSave,
    userSavedListings: store.userSavedListings,
    isInitialized: store.isInitialized,
    initialize: store.initialize,
    cleanup: store.cleanup,
  };
};

// React Hook for save status of a specific listing
export const useListingSave = (listingId) => {
  const { isSaved, toggleSave, isInitialized } = useSave();
  return {
    isSaved: isSaved(listingId),
    toggleSave: (params) => toggleSave({ ...params, listingId }),
    isInitialized,
  };
};

// Context Provider setup
const SaveContext = createContext(null);

export const SaveProvider = ({ children }) => {
  const store = useSaveStore();

  useEffect(() => {
    store.initialize();
    return () => {
      store.cleanup();
    };
  }, []);

  return (
    <SaveContext.Provider value={store}>
      {children}
    </SaveContext.Provider>
  );
};

export const useSaveContext = () => {
  const context = useContext(SaveContext);
  if (!context) {
    throw new Error('useSaveContext must be used within SaveProvider');
  }
  return context;
};

export default {
  SaveProvider,
  useSave,
  useSaveContext,
  useListingSave,
};